"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";

interface AccordionItem {
  id: number;
  title: string;
  subtitle?: string;
  content?: string;
}

interface AccordionListProps {
  data: AccordionItem[];
  defaultOpenFirst?: boolean;
  className?: string;
}

export default function AccordionList({data, defaultOpenFirst = true, className = ""}: AccordionListProps) {
    const [activeId, setActiveId] = useState<number | null>(null);

    // Optionally open the first accordion item by default
    useEffect(() => {
      if (defaultOpenFirst && data.length > 0) {
        setActiveId(data[0].id);
      }
    }, [data, defaultOpenFirst]);

    return (
      <div className={`flex flex-col w-full mx-auto ${className}`}>
        {data.map((item) => {
          const isActive = activeId === item.id;
          return (
            <motion.div
              key={item.id}
              onClick={() => setActiveId(isActive ? null : item.id)}
              className={`border-b border-gray-200 py-6 md:px-6 cursor-pointer transition-all duration-300 rounded-2xl
                ${isActive ? "bg-white shadow-md" : "bg-transparent hover:bg-gray-50"}`}
              layout
            >
              {/* Header */}
              <div className="flex items-start justify-between px-2">
                <div className="flex items-center gap-8">
                  <span className="text-primary text-4xl font-semibold w-10">
                    {String(item.id).padStart(2, "0")}
                  </span>
                  <h3
                    className={`text-base md:text-xl font-semibold transition-colors duration-300 ${
                      isActive ? "text-accent" : "text-gray-900"
                    }`}
                  >
                    {item.title}
                  </h3>
                </div>

                <motion.div
                  animate={{ rotate: isActive ? 90 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ArrowRight className="w-6 h-6 text-gray-500" />
                </motion.div>
              </div>

              {/* Description */}
              <AnimatePresence>
                {isActive && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="text-gray-600 ml-20 mt-3 max-w-3xl leading-relaxed">
                      {item.content}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    );
}
